import { useEffect, useRef } from 'react';
import { SHEET } from '../design/motion';

// iOS-style bottom sheet for the public bill dialogs on phone widths: the sheet follows the
// finger, rubber-bands past its limits and springs with momentum to collapsed, expanded or
// dismissed. Positions are the sheet's visible height in px.
//
// Only `transform` moves while a finger is down or a spring is running: the sheet keeps the height
// it was laid out with and is translated from there (a same-colour box-shadow below it covers the
// rubber band's lift). Pointer positions are coalesced to one write per animation frame and the
// backdrop's opacity is written on that same frame. The laid-out height grows to the expanded height
// on the frame a collapsed sheet first rises past it (a write on that frame, never a read), so the
// lift reveals content rather than a blank strip, and shrinks back once the sheet is at rest. Nothing
// here forces a layout mid-gesture.
//
// Scrolling the content is the browser's: a touch that can only scroll registers no listener the
// browser has to wait for, and a touch that could become a drag drops its scroll-blocking listener
// the moment the gesture turns out to be a scroll (#66).
//
// Whether a downward finger pulls the sheet is settled where the touch lands, not where the content
// is by the time the finger has moved: a finger that lands while a scroll is still carrying the content
// to its top is a scroll, however far it then pulls, and only a finger that lands with every scroller
// already at its top drags the sheet (#181: a fling that merely reached the top used to hand off to a
// drag and snap an expanded sheet to its collapsed detent).
//
// The sheet's CSS entry animation is never switched off and on again: that replays it, and the sheet
// slid in from the bottom a second time after every release (#66 re-report). It animates `translate`,
// apart from the `transform` written here, and a finger that lands while it is still running finishes
// it, so the gesture starts from where the sheet was about to rest.
//
// The detent is written to the sheet as `data-detent` from here, not held as React state: a release
// then commits no render at all (a re-render of the dialog shell made its library re-attach refs and
// commit twice more), and the attribute is the only thing the detent drives.
//
// The sheet is laid out against the layout viewport (`innerHeight`), which is what `bottom: 0` anchors to.
// A browser whose own bars cover the bottom of that viewport (iOS 26 Safari with viewport-fit=cover lays
// the page out under its floating toolbar, and env(safe-area-inset-bottom) reports only the home-indicator
// strip; an on-screen keyboard covers it the same way) leaves the covered height out of the visual viewport,
// and that height is written to the sheet as `--vs-sheet-chrome-inset` for the footer's clearance (#181
// round 2: the sheet's last action sat under the toolbar, cut off and unreachable).

export type SheetDetent = 'collapsed' | 'expanded';
export type SheetTarget = SheetDetent | 'dismissed';
export type SpringConfig = { mass: number; stiffness: number; damping: number };

// Spring integration: semi-implicit Euler in sub-steps no longer than this, which keeps the
// tokenised spring (stiffness 320, damping 32, mass 1 → damping ratio ≈ 0.9) stable at any frame
// rate. A frame longer than the clamp (a background tab, a dropped frame burst) is treated as
// one long step rather than a teleport.
const SPRING_SUBSTEP_MS = 4;
const FRAME_CLAMP_MS = 64;
// Rest: slower than 10 px/s and within half a pixel of the target.
const REST_VELOCITY_PX_PER_S = 10;
const REST_DISTANCE_PX = 0.5;
// A move shorter than this is a tap, not a direction: the gesture stays undecided.
const DECIDE_SLOP_PX = 3;

/** Resistance past a limit: each extra pixel moves the sheet less (the UIKit curve). */
export function rubberBand(overshoot: number, limit: number = SHEET.rubberBandLimitPx, ratio: number = SHEET.rubberBandRatio) {
  if (overshoot <= 0) return 0;
  return (1 - 1 / ((overshoot * ratio) / limit + 1)) * limit;
}

/** Where a flick would come to rest; velocity in px/ms (UIScrollView's projection). */
export function projectPosition(position: number, velocity: number, decelerationRate: number = SHEET.decelerationRate) {
  return position + (velocity * decelerationRate) / (1 - decelerationRate);
}

/** Snap target for a projected visible height. A sheet released from expanded needs a real flick to dismiss. */
export function chooseDetent(projected: number, collapsed: number, expanded: number | null, canDismiss: boolean, from: SheetDetent): SheetTarget {
  if (canDismiss && projected < collapsed * (from === 'expanded' ? 0.15 : 0.5)) return 'dismissed';
  if (expanded !== null && projected > (collapsed + expanded) / 2) return 'expanded';
  return 'collapsed';
}

/** Velocity (px/ms, positive = sheet growing) from the last 100ms of samples. */
export function releaseVelocity(samples: { t: number; y: number }[]) {
  const first = samples[0], last = samples[samples.length - 1];
  if (!first || !last || last.t <= first.t) return 0;
  return (last.y - first.y) / (last.t - first.t);
}

/** Advance a damped spring (position px, velocity px/s) toward `target` by `dtMs`. */
export function stepSpring(x: number, v: number, target: number, dtMs: number, spring: SpringConfig = SHEET.spring) {
  const steps = Math.max(1, Math.ceil(dtMs / SPRING_SUBSTEP_MS));
  const dt = dtMs / steps / 1000;
  for (let i = 0; i < steps; i++) {
    const acceleration = (-spring.stiffness * (x - target) - spring.damping * v) / spring.mass;
    v += acceleration * dt;
    x += v * dt;
  }
  return { x, v };
}

/** True once the spring is close enough and slow enough to stop. */
export function springAtRest(x: number, v: number, target: number) {
  return Math.abs(v) < REST_VELOCITY_PX_PER_S && Math.abs(x - target) < REST_DISTANCE_PX;
}

export function isSheetLayout() {
  return typeof window.matchMedia !== 'function' || window.matchMedia(`(max-width: ${SHEET.singleColumnPx}px)`).matches;
}

function reducedMotion() {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function viewportHeight() {
  return window.innerHeight;
}

/** How much of the bottom of the layout viewport the browser's own bars (or the keyboard) cover, in px. */
export function chromeInset(viewport: { height: number; offsetTop: number; scale: number } | null | undefined = window.visualViewport, layoutHeight: number = window.innerHeight) {
  // A pinched-in view is a window onto the layout viewport, not a bar over it.
  if (!viewport || viewport.scale > 1.01) return 0;
  return Math.max(0, Math.round(layoutHeight - viewport.offsetTop - viewport.height));
}

/** The scroll containers between a touch and the sheet, the sheet itself included (it is the dialog's scroller). */
export function scrollersBetween(target: EventTarget | null, root: HTMLElement) {
  const scrollers: Element[] = [];
  let node = target instanceof Element ? target : null;
  while (node && node !== root) {
    const overflow = window.getComputedStyle(node).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll') && node.scrollHeight > node.clientHeight + 1) scrollers.push(node);
    node = node.parentElement;
  }
  scrollers.push(root);
  return scrollers;
}

type Gesture = {
  startClientX: number; startClientY: number; startY: number; latestY: number;
  mode: 'pending' | 'drag' | 'scroll'; moved: boolean; from: SheetDetent; handle: boolean;
  // Every scroll container under the touch was at its top when the finger landed (a bounce past the top counts).
  atTop: boolean;
  scrollers: Element[]; samples: { t: number; y: number }[];
};

// Takes the elements, not refs: the dialog portal mounts its content a commit after it opens,
// so the host passes callback refs and this effect re-runs once the sheet exists.
export default function useBottomSheet(element: HTMLElement | null, options: { enabled: boolean; locked?: boolean; backdrop?: HTMLElement | null; onDismiss: () => void }) {
  const live = useRef({ detent: 'collapsed' as SheetDetent, collapsed: 0, expanded: null as number | null, layout: 0, y: 0, locked: false, onDismiss: options.onDismiss });
  live.current.locked = !!options.locked;
  live.current.onDismiss = options.onDismiss;
  const { enabled, backdrop = null } = options;

  useEffect(() => {
    if (!enabled || !element) return;
    const state = live.current;
    const setDetent = (detent: SheetDetent) => { state.detent = detent; element.dataset.detent = detent; };
    setDetent('collapsed');
    let gesture: Gesture | null = null;
    let frame = 0, pendingY: number | null = null;
    let settle: (() => void) | null = null;

    const measure = () => {
      const current = element.getBoundingClientRect().height;
      const collapsed = state.detent === 'expanded' && state.collapsed ? state.collapsed : current;
      const expanded = viewportHeight() - SHEET.topInsetPx;
      return { current, collapsed, expanded: expanded - collapsed > SHEET.minExpansionPx ? expanded : null };
    };
    // The one place that touches the DOM per frame: a translate from the laid-out height, and the backdrop.
    // A collapsed sheet rising past its laid-out height is laid out at its expanded height on that frame.
    const render = (y: number) => {
      const top = state.expanded ?? state.collapsed;
      const visible = y > top ? top + rubberBand(y - top) : y;
      if (state.expanded !== null && state.layout < state.expanded && visible > state.layout) { element.style.height = `${state.expanded}px`; state.layout = state.expanded; }
      const offset = Math.round((state.layout - visible) * 100) / 100;
      element.style.transform = offset ? `translateY(${offset}px)` : '';
      if (backdrop) backdrop.style.opacity = String(Math.min(1, Math.max(0, visible / (state.collapsed || 1))));
      state.y = y;
    };
    // One render per frame: the latest position wins, and a frame is only requested when nothing is queued.
    const flush = () => { frame = 0; if (pendingY === null) return; const y = pendingY; pendingY = null; render(y); };
    const schedule = (y: number) => { const queued = pendingY !== null; pendingY = y; if (!queued) frame = window.requestAnimationFrame(flush); };

    // Interrupting a spring keeps the sheet where it is, like catching it mid-flight.
    const cancelSettle = () => {
      if (!settle) return false;
      const stop = settle; settle = null; stop();
      element.classList.remove('bill-sheet--snapping', 'bill-sheet--dismissed');
      backdrop?.classList.remove('bill-dialog-overlay--dismissed');
      return true;
    };
    // At rest the laid-out height settles on the detent's: already the expanded height after a lift, and back
    // to the content's own height for a collapsed sheet, whose top edge stays put either way.
    const commit = (target: SheetDetent) => {
      if (target === 'expanded' && state.expanded !== null) { element.style.height = `${state.expanded}px`; state.layout = state.expanded; }
      else { element.style.height = ''; state.layout = state.collapsed; }
      element.style.transform = '';
      if (backdrop) backdrop.style.opacity = '';
      state.y = state.layout;
      setDetent(target);
    };
    const spring = (fromY: number, velocityPxPerMs: number, toY: number, done: () => void) => {
      let x = fromY, v = velocityPxPerMs * 1000, last: number | null = null, started: number | null = null, id = 0;
      const tick = (now: number) => {
        if (started === null) started = now;
        const dt = last === null ? 1000 / 60 : Math.min(FRAME_CLAMP_MS, Math.max(0, now - last));
        last = now;
        ({ x, v } = stepSpring(x, v, toY, dt));
        if (springAtRest(x, v, toY) || now - started > SHEET.settleMaxDurationMs) { settle = null; done(); return; }
        render(x);
        id = window.requestAnimationFrame(tick);
      };
      settle = () => window.cancelAnimationFrame(id);
      id = window.requestAnimationFrame(tick);
    };
    const snap = (target: SheetTarget, fromY: number, velocity: number) => {
      const toY = target === 'dismissed' ? -SHEET.topInsetPx : target === 'expanded' ? state.expanded ?? state.collapsed : state.collapsed;
      if (target === 'dismissed') { element.classList.add('bill-sheet--dismissed'); backdrop?.classList.add('bill-dialog-overlay--dismissed'); }
      const finish = () => {
        render(toY);
        element.classList.remove('bill-sheet--snapping');
        if (target === 'dismissed') { state.onDismiss(); return; }
        commit(target);
      };
      if (reducedMotion()) { finish(); return; }
      element.classList.add('bill-sheet--snapping');
      spring(fromY, velocity, toY, finish);
    };
    // A mouse drag would otherwise sweep a text selection across the sheet.
    const clearSelection = () => { try { window.getSelection()?.removeAllRanges(); } catch { /* selection API unavailable */ } };
    const suppressNextClick = () => {
      const suppress = (event: Event) => { event.stopPropagation(); event.preventDefault(); stop(); };
      const stop = () => { element.removeEventListener('click', suppress, true); window.clearTimeout(timer); };
      const timer = window.setTimeout(stop, 400);
      element.addEventListener('click', suppress, true);
    };

    // Mirrors `decide` from where a touch starts: only a touch that could become a drag needs a listener the
    // browser must wait for before it scrolls.
    const couldDrag = (current: Gesture) =>
      current.handle
      || current.atTop
      || (state.detent === 'collapsed' && state.expanded !== null)
      || !current.scrollers.some((scroller) => scroller.scrollHeight > scroller.clientHeight + 1);
    let blocking = false;
    const stopBlocking = () => { if (!blocking) return; blocking = false; element.removeEventListener('touchmove', onTouchMove); };
    // A finger that lands during the entry animation finishes it; the hook then measures the sheet at rest.
    const finishAnimations = () => {
      for (const node of [element, backdrop]) {
        if (!node || typeof node.getAnimations !== 'function') continue;
        for (const animation of node.getAnimations()) if (animation.playState === 'running') animation.finish();
      }
    };
    const begin = (clientX: number, clientY: number, target: EventTarget | null) => {
      if (gesture || !isSheetLayout()) return;
      finishAnimations();
      // A caught sheet keeps the detents it was measured with and starts from where the spring left it.
      const interrupted = cancelSettle();
      if (!interrupted) { const measured = measure(); state.collapsed = measured.collapsed; state.expanded = measured.expanded; state.layout = measured.current; state.y = measured.current; }
      const handle = target instanceof Element && !!target.closest('[data-sheet-handle]');
      const scrollers = handle ? [] : scrollersBetween(target, element);
      gesture = { startClientX: clientX, startClientY: clientY, startY: state.y, latestY: state.y, mode: 'pending', moved: false, from: state.detent, handle, atTop: scrollers.every((scroller) => scroller.scrollTop <= 0), scrollers, samples: [] };
    };
    // The handle and the heading always drag. Elsewhere, a finger down drags only when every scroll
    // container under it was already at its top when the finger landed (#181: content that a fling carries
    // to the top under a finger keeps scrolling); a finger up expands a collapsed sheet, and otherwise
    // scrolls whatever still has overflow.
    const decide = (current: Gesture, dx: number, dy: number): Gesture['mode'] => {
      if (Math.abs(dx) > Math.abs(dy)) return 'scroll';
      if (current.handle) return 'drag';
      if (dy > 0) return current.atTop ? 'drag' : 'scroll';
      if (state.detent === 'collapsed' && state.expanded !== null) return 'drag';
      return current.scrollers.some((scroller) => scroller.scrollHeight > scroller.clientHeight + 1) ? 'scroll' : 'drag';
    };
    const move = (clientX: number, clientY: number, prevent: () => void) => {
      if (!gesture) return;
      const dx = clientX - gesture.startClientX, dy = clientY - gesture.startClientY;
      if (gesture.mode === 'pending') {
        if (Math.hypot(dx, dy) < DECIDE_SLOP_PX) return;
        gesture.mode = decide(gesture, dx, dy);
        if (gesture.mode === 'drag') { element.classList.add('bill-sheet--dragging'); clearSelection(); }
        else stopBlocking();
      }
      if (gesture.mode !== 'drag') return;
      prevent();
      if (Math.abs(dy) > SHEET.tapSlopPx) gesture.moved = true;
      const y = gesture.startY - dy, now = performance.now();
      gesture.samples.push({ t: now, y });
      while (gesture.samples.length > 1 && now - gesture.samples[0].t > 100) gesture.samples.shift();
      gesture.latestY = y;
      schedule(y);
    };
    const end = () => {
      if (!gesture) return;
      const finished = gesture;
      gesture = null;
      stopBlocking();
      if (frame) { window.cancelAnimationFrame(frame); frame = 0; }
      pendingY = null;
      if (finished.mode !== 'drag') return;
      element.classList.remove('bill-sheet--dragging');
      clearSelection();
      render(finished.latestY);
      const velocity = releaseVelocity(finished.samples);
      const target = chooseDetent(projectPosition(finished.latestY, velocity), state.collapsed, state.expanded, !state.locked, finished.from);
      if (finished.moved) suppressNextClick();
      snap(target, finished.latestY, velocity);
    };

    function onTouchMove(event: TouchEvent) { if (event.touches.length !== 1) { end(); return; } move(event.touches[0].clientX, event.touches[0].clientY, () => { if (event.cancelable) event.preventDefault(); }); }
    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) { end(); return; }
      begin(event.touches[0].clientX, event.touches[0].clientY, event.target);
      if (gesture && !blocking && couldDrag(gesture)) { blocking = true; element.addEventListener('touchmove', onTouchMove, { passive: false }); }
    };
    const onPointerDown = (event: PointerEvent) => { if (event.pointerType !== 'mouse' || event.button !== 0) return; begin(event.clientX, event.clientY, event.target); };
    const onPointerMove = (event: PointerEvent) => { if (event.pointerType !== 'mouse') return; move(event.clientX, event.clientY, () => event.preventDefault()); };
    const onPointerUp = (event: PointerEvent) => { if (event.pointerType === 'mouse') end(); };
    const syncChrome = () => { element.style.setProperty('--vs-sheet-chrome-inset', `${chromeInset()}px`); };
    const onResize = () => {
      syncChrome();
      if (state.detent !== 'expanded' || gesture || settle) return;
      state.expanded = viewportHeight() - SHEET.topInsetPx;
      element.style.height = `${state.expanded}px`;
      state.layout = state.expanded; state.y = state.layout;
    };
    syncChrome();

    element.addEventListener('touchstart', onTouchStart, { passive: true });
    element.addEventListener('touchend', end);
    element.addEventListener('touchcancel', end);
    element.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    window.addEventListener('resize', onResize);
    const visual = window.visualViewport;
    visual?.addEventListener('resize', syncChrome);
    visual?.addEventListener('scroll', syncChrome);
    return () => {
      element.removeEventListener('touchstart', onTouchStart);
      stopBlocking();
      element.removeEventListener('touchend', end);
      element.removeEventListener('touchcancel', end);
      element.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      window.removeEventListener('resize', onResize);
      visual?.removeEventListener('resize', syncChrome);
      visual?.removeEventListener('scroll', syncChrome);
      element.style.removeProperty('--vs-sheet-chrome-inset');
      if (frame) window.cancelAnimationFrame(frame);
      settle?.();
      element.classList.remove('bill-sheet--dragging', 'bill-sheet--snapping');
      element.style.height = '';
      element.style.transform = '';
      delete element.dataset.detent;
      if (backdrop) { backdrop.style.opacity = ''; backdrop.classList.remove('bill-dialog-overlay--dismissed'); }
    };
  }, [enabled, element, backdrop]);
}
